import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ModuleAccessGuard } from '@/components/ModuleAccessGuard';
import { PDFPreview } from '@/components/shared/PDFPreview';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useParserJob, useManualParseSubmit } from '@/hooks/useParserJobs';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ManualParseDetail = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();

  const { data: job, isLoading } = useParserJob(jobId!);
  const manualParseSubmit = useManualParseSubmit();

  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_date: '',
    amount_due: '',
    due_date: '',
    billing_period_from: '',
    billing_period_to: '',
    usage_amount: '',
    usage_unit: '',
    comments: '',
    progress_status: 'in_progress',
  });

  useEffect(() => {
    if (job) {
      setFormData({
        invoice_number: job.parsed_data?.invoice_number || '',
        invoice_date: job.parsed_data?.invoice_date || '',
        amount_due: job.parsed_data?.amount_due?.toString() || '',
        due_date: job.parsed_data?.due_date || '',
        billing_period_from: job.parsed_data?.billing_period_from || '',
        billing_period_to: job.parsed_data?.billing_period_to || '',
        usage_amount: job.parsed_data?.usage_amount?.toString() || '',
        usage_unit: job.parsed_data?.usage_unit || '',
        comments: job.comments || '',
        progress_status: job.progress_status || 'in_progress',
      });
    }
  }, [job]);

  const handleSaveProgress = async () => {
    if (!jobId) return;

    const parsedData = {
      invoice_number: formData.invoice_number,
      invoice_date: formData.invoice_date,
      amount_due: parseFloat(formData.amount_due) || 0,
      due_date: formData.due_date,
      billing_period_from: formData.billing_period_from,
      billing_period_to: formData.billing_period_to,
      usage_amount: parseFloat(formData.usage_amount) || 0,
      usage_unit: formData.usage_unit,
    };

    try {
      await manualParseSubmit.mutateAsync({
        jobId: jobId!,
        parsedData,
        isDraft: true,
      });
      toast({
        title: 'Progress saved',
        description: 'Your work has been saved as a draft',
      });
    } catch (error: any) {
      toast({
        title: 'Save failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSubmitComplete = async () => {
    if (!jobId) return;

    // Validate required fields
    if (!formData.invoice_number || !formData.invoice_date || !formData.amount_due) {
      toast({
        title: 'Validation error',
        description: 'Invoice Number, Invoice Date, and Amount Due are required',
        variant: 'destructive',
      });
      return;
    }

    const parsedData = {
      invoice_number: formData.invoice_number,
      invoice_date: formData.invoice_date,
      amount_due: parseFloat(formData.amount_due) || 0,
      due_date: formData.due_date,
      billing_period_from: formData.billing_period_from,
      billing_period_to: formData.billing_period_to,
      usage_amount: parseFloat(formData.usage_amount) || 0,
      usage_unit: formData.usage_unit,
    };

    try {
      await manualParseSubmit.mutateAsync({
        jobId: jobId!,
        parsedData,
        isDraft: false,
      });
      toast({
        title: 'Success',
        description: 'Bill data submitted successfully',
      });
      navigate(`/w/${currentWorkspace?.id}/dashboard/parser/manual-queue`);
    } catch (error: any) {
      toast({
        title: 'Submit failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <ModuleAccessGuard moduleId="bill_processing">
        <div className="container mx-auto py-6 flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </ModuleAccessGuard>
    );
  }

  if (!job) {
    return (
      <ModuleAccessGuard moduleId="bill_processing">
        <div className="container mx-auto py-6">
          <div className="text-center">
            <p className="text-muted-foreground">Job not found</p>
            <Button onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </ModuleAccessGuard>
    );
  }

  return (
    <ModuleAccessGuard moduleId="bill_processing">
      <div className="container mx-auto py-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Queue
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: PDF Preview */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardContent className="p-4">
                <PDFPreview fileUrl={job.file_url} />
              </CardContent>
            </Card>
          </div>

          {/* Right: Manual Entry Form */}
          <div className="lg:col-span-1">
            <Card className="h-full overflow-y-auto">
              <CardHeader>
                <CardTitle>Manual Data Entry</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Invoice Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Invoice Information</h3>
                  <div className="space-y-2">
                    <Label>Invoice Number *</Label>
                    <Input
                      value={formData.invoice_number}
                      onChange={(e) =>
                        setFormData({ ...formData, invoice_number: e.target.value })
                      }
                      placeholder="Enter invoice number"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Invoice Date *</Label>
                    <Input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) =>
                        setFormData({ ...formData, invoice_date: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Amount Due *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount_due}
                      onChange={(e) =>
                        setFormData({ ...formData, amount_due: e.target.value })
                      }
                      placeholder="0.00"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    />
                  </div>
                </div>

                <Separator />

                {/* Billing Period */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Billing Period</h3>
                  <div className="space-y-2">
                    <Label>Period From</Label>
                    <Input
                      type="date"
                      value={formData.billing_period_from}
                      onChange={(e) =>
                        setFormData({ ...formData, billing_period_from: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Period To</Label>
                    <Input
                      type="date"
                      value={formData.billing_period_to}
                      onChange={(e) =>
                        setFormData({ ...formData, billing_period_to: e.target.value })
                      }
                    />
                  </div>
                </div>

                <Separator />

                {/* Usage */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Usage</h3>
                  <div className="space-y-2">
                    <Label>Usage Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.usage_amount}
                      onChange={(e) =>
                        setFormData({ ...formData, usage_amount: e.target.value })
                      }
                      placeholder="0.00"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Usage Unit</Label>
                    <Input
                      value={formData.usage_unit}
                      onChange={(e) =>
                        setFormData({ ...formData, usage_unit: e.target.value })
                      }
                      placeholder="kWh, gallons, etc."
                    />
                  </div>
                </div>

                <Separator />

                {/* Comments & Status */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Comments</Label>
                    <Textarea
                      value={formData.comments}
                      onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                      placeholder="Add any notes or comments..."
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Progress Status</Label>
                    <Select
                      value={formData.progress_status}
                      onValueChange={(v) => setFormData({ ...formData, progress_status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todo">To Do</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Separator />

                {/* Action Buttons */}
                <div className="space-y-2">
                  <Button
                    onClick={handleSaveProgress}
                    disabled={manualParseSubmit.isPending}
                    variant="outline"
                    className="w-full"
                  >
                    {manualParseSubmit.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Progress (Draft)'
                    )}
                  </Button>
                  <Button
                    onClick={handleSubmitComplete}
                    disabled={manualParseSubmit.isPending}
                    className="w-full"
                  >
                    {manualParseSubmit.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit & Complete'
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ModuleAccessGuard>
  );
};

export default ManualParseDetail;
